import {
  TEAM_BILLING_CACHE_FIELDS,
  Team,
  TeamInvite,
  TeamRole,
} from '@this+that/models';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { TeamBilling } from '../../services/billing.service';

export const TEAM_CACHE_TTL_MS = 60 * 1000;
export const OPTIMISTIC_TEAM_ID_TTL_MS = 2 * 60 * 1000;

const TEAM_STORE_PERSIST_KEY = 'team-store';

interface CachedTeam {
  team: Team;
  members: Team[];
  loadedAt: number;
}

interface CachedInvites {
  items: TeamInvite[];
  loadedAt: number;
}

interface CachedTeamBilling {
  billing: TeamBilling;
  loadedAt: number;
}

interface TeamState {
  activeTeamId: string | null;
  userTeamIds: string[] | null;
  userTeamIdsLoadedAt: number | null;
  optimisticTeamIds: Record<string, number>;
  teamsById: Record<string, CachedTeam>;
  pendingInvites: CachedInvites | null;
  invitesByTeamId: Record<string, CachedInvites>;
  acceptingInviteIds: Record<string, true>;
  billingByTeamId: Record<string, CachedTeamBilling>;
}

interface TeamActions {
  setActiveTeamId: (teamId: string) => void;
  setUserTeamIds: (ids: string[]) => void;
  addUserTeamId: (teamId: string) => void;
  setTeamData: (teamId: string, team: Team, members: Team[]) => void;
  setTeamMembers: (teamId: string, members: Team[]) => void;
  patchTeam: (teamId: string, patch: Partial<Team>) => void;
  setMemberRole: (teamId: string, userId: string, role: TeamRole) => void;
  upsertTeamMember: (teamId: string, member: Team) => void;
  removeMemberFromTeam: (teamId: string, userId: string) => void;
  setPendingInvites: (items: TeamInvite[]) => void;
  removePendingInvite: (teamId: string) => void;
  prependPendingInvite: (invite: TeamInvite) => void;
  setTeamInvites: (teamId: string, items: TeamInvite[]) => void;
  upsertTeamInvites: (teamId: string, invites: TeamInvite[]) => void;
  removeTeamInvite: (teamId: string, inviteeEmail: string) => void;
  removeTeam: (teamId: string) => void;
  invalidateUserTeamIds: () => void;
  beginAcceptingInvite: (teamId: string) => void;
  endAcceptingInvite: (teamId: string) => void;
  setTeamBilling: (teamId: string, billing: TeamBilling) => void;
  invalidateTeamBilling: (teamId: string) => void;
  resetState: () => void;
}

export type TeamStore = TeamState & TeamActions;

export function isTeamCacheStale(
  loadedAt: number | null | undefined,
  maxAge: number = TEAM_CACHE_TTL_MS,
): boolean {
  if (!loadedAt) {
    return true;
  }
  return Date.now() - loadedAt > maxAge;
}

function resolveActiveTeamId(
  currentActiveTeamId: string | null,
  ids: string[],
): string | null {
  const isCurrentValid =
    currentActiveTeamId !== null && ids.includes(currentActiveTeamId);
  return isCurrentValid ? currentActiveTeamId : (ids[0] ?? null);
}

function mergeServerTeamIds(
  ids: string[],
  optimisticTeamIds: Record<string, number>,
): { userTeamIds: string[]; optimisticTeamIds: Record<string, number> } {
  const now = Date.now();
  const unconfirmed = Object.entries(optimisticTeamIds).filter(
    ([teamId, markedAt]) =>
      !ids.includes(teamId) && now - markedAt <= OPTIMISTIC_TEAM_ID_TTL_MS,
  );
  return {
    userTeamIds: [...ids, ...unconfirmed.map(([teamId]) => teamId)],
    optimisticTeamIds: Object.fromEntries(unconfirmed),
  };
}

const INITIAL_TEAM_STATE: TeamState = {
  activeTeamId: null,
  userTeamIds: null,
  userTeamIdsLoadedAt: null,
  optimisticTeamIds: {},
  teamsById: {},
  pendingInvites: null,
  invitesByTeamId: {},
  acceptingInviteIds: {},
  billingByTeamId: {},
};

function patchTouchesBillingCache(patch: Partial<Team>): boolean {
  return TEAM_BILLING_CACHE_FIELDS.some((field) => field in patch);
}

export const useTeamStore = create<TeamStore>()(
  persist(
    (set) => ({
      ...INITIAL_TEAM_STATE,

      setActiveTeamId: (teamId) => set({ activeTeamId: teamId }),

      setUserTeamIds: (ids) =>
        set((state) => {
          const merged = mergeServerTeamIds(ids, state.optimisticTeamIds);
          return {
            userTeamIds: merged.userTeamIds,
            optimisticTeamIds: merged.optimisticTeamIds,
            userTeamIdsLoadedAt: Date.now(),
            activeTeamId: resolveActiveTeamId(
              state.activeTeamId,
              merged.userTeamIds,
            ),
          };
        }),

      addUserTeamId: (teamId) =>
        set((state) => {
          if (state.userTeamIds?.includes(teamId)) {
            return state;
          }
          const userTeamIds = state.userTeamIds
            ? [...state.userTeamIds, teamId]
            : [teamId];
          return {
            userTeamIds,
            optimisticTeamIds: {
              ...state.optimisticTeamIds,
              [teamId]: Date.now(),
            },
            activeTeamId: resolveActiveTeamId(state.activeTeamId, userTeamIds),
          };
        }),

      setTeamData: (teamId, team, members) =>
        set((state) => ({
          teamsById: {
            ...state.teamsById,
            [teamId]: { team, members, loadedAt: Date.now() },
          },
        })),

      setTeamMembers: (teamId, members) =>
        set((state) => {
          const existing = state.teamsById[teamId];
          if (!existing) {
            return state;
          }
          return {
            teamsById: {
              ...state.teamsById,
              [teamId]: { ...existing, members },
            },
          };
        }),

      patchTeam: (teamId, patch) =>
        set((state) => {
          const existing = state.teamsById[teamId];
          if (!existing) {
            return state;
          }
          const teamsById = {
            ...state.teamsById,
            [teamId]: { ...existing, team: { ...existing.team, ...patch } },
          };
          if (!patchTouchesBillingCache(patch)) {
            return { teamsById };
          }
          const billingByTeamId = { ...state.billingByTeamId };
          delete billingByTeamId[teamId];
          return { teamsById, billingByTeamId };
        }),

      setMemberRole: (teamId, userId, role) =>
        set((state) => {
          const existing = state.teamsById[teamId];
          if (!existing) {
            return state;
          }
          return {
            teamsById: {
              ...state.teamsById,
              [teamId]: {
                ...existing,
                members: existing.members.map((m) =>
                  m.userId === userId ? { ...m, userRole: role } : m,
                ),
              },
            },
          };
        }),

      upsertTeamMember: (teamId, member) =>
        set((state) => {
          const existing = state.teamsById[teamId];
          if (!existing) {
            return state;
          }
          const found = existing.members.some(
            (m) => m.userId === member.userId,
          );
          const members = found
            ? existing.members.map((m) =>
                m.userId === member.userId ? { ...m, ...member } : m,
              )
            : [...existing.members, member];
          return {
            teamsById: {
              ...state.teamsById,
              [teamId]: { ...existing, members },
            },
          };
        }),

      removeMemberFromTeam: (teamId, userId) =>
        set((state) => {
          const existing = state.teamsById[teamId];
          if (!existing) {
            return state;
          }
          return {
            teamsById: {
              ...state.teamsById,
              [teamId]: {
                ...existing,
                members: existing.members.filter((m) => m.userId !== userId),
              },
            },
          };
        }),

      setPendingInvites: (items) =>
        set({ pendingInvites: { items, loadedAt: Date.now() } }),

      removePendingInvite: (teamId) =>
        set((state) =>
          state.pendingInvites
            ? {
                pendingInvites: {
                  ...state.pendingInvites,
                  items: state.pendingInvites.items.filter(
                    (inv) => inv.teamId !== teamId,
                  ),
                },
              }
            : state,
        ),

      prependPendingInvite: (invite) =>
        set((state) => {
          if (state.pendingInvites === null) {
            return {
              pendingInvites: { items: [invite], loadedAt: Date.now() },
            };
          }
          if (
            state.pendingInvites.items.some((i) => i.teamId === invite.teamId)
          ) {
            return state;
          }
          return {
            pendingInvites: {
              ...state.pendingInvites,
              items: [invite, ...state.pendingInvites.items],
            },
          };
        }),

      setTeamInvites: (teamId, items) =>
        set((state) => ({
          invitesByTeamId: {
            ...state.invitesByTeamId,
            [teamId]: { items, loadedAt: Date.now() },
          },
        })),

      upsertTeamInvites: (teamId, invites) =>
        set((state) => {
          const existing = state.invitesByTeamId[teamId];
          const byEmail = new Map(
            (existing?.items ?? []).map((inv) => [inv.inviteeEmail, inv]),
          );
          invites.forEach((inv) => byEmail.set(inv.inviteeEmail, inv));
          return {
            invitesByTeamId: {
              ...state.invitesByTeamId,
              [teamId]: {
                items: Array.from(byEmail.values()),
                loadedAt: existing?.loadedAt ?? Date.now(),
              },
            },
          };
        }),

      removeTeamInvite: (teamId, inviteeEmail) =>
        set((state) => {
          const existing = state.invitesByTeamId[teamId];
          if (!existing) {
            return state;
          }
          return {
            invitesByTeamId: {
              ...state.invitesByTeamId,
              [teamId]: {
                ...existing,
                items: existing.items.filter(
                  (inv) => inv.inviteeEmail !== inviteeEmail,
                ),
              },
            },
          };
        }),

      removeTeam: (teamId) =>
        set((state) => {
          const teamsById = { ...state.teamsById };
          delete teamsById[teamId];
          const invitesByTeamId = { ...state.invitesByTeamId };
          delete invitesByTeamId[teamId];
          const optimisticTeamIds = { ...state.optimisticTeamIds };
          delete optimisticTeamIds[teamId];
          const billingByTeamId = { ...state.billingByTeamId };
          delete billingByTeamId[teamId];
          const remainingIds =
            state.userTeamIds?.filter((id) => id !== teamId) ?? null;
          const activeTeamId =
            state.activeTeamId === teamId
              ? (remainingIds?.[0] ?? null)
              : state.activeTeamId;
          return {
            teamsById,
            invitesByTeamId,
            optimisticTeamIds,
            billingByTeamId,
            userTeamIds: remainingIds,
            activeTeamId,
          };
        }),

      invalidateUserTeamIds: () =>
        set({ userTeamIds: null, userTeamIdsLoadedAt: null }),

      beginAcceptingInvite: (teamId) =>
        set((state) => ({
          acceptingInviteIds: { ...state.acceptingInviteIds, [teamId]: true },
        })),

      endAcceptingInvite: (teamId) =>
        set((state) => {
          const acceptingInviteIds = { ...state.acceptingInviteIds };
          delete acceptingInviteIds[teamId];
          return { acceptingInviteIds };
        }),

      setTeamBilling: (teamId, billing) =>
        set((state) => ({
          billingByTeamId: {
            ...state.billingByTeamId,
            [teamId]: { billing, loadedAt: Date.now() },
          },
        })),

      invalidateTeamBilling: (teamId) =>
        set((state) => {
          if (!state.billingByTeamId[teamId]) {
            return state;
          }
          const billingByTeamId = { ...state.billingByTeamId };
          delete billingByTeamId[teamId];
          return { billingByTeamId };
        }),

      resetState: () => set(INITIAL_TEAM_STATE),
    }),
    {
      name: TEAM_STORE_PERSIST_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ activeTeamId: state.activeTeamId }),
    },
  ),
);

// Resolves to the active team, falling back to the user's first team — so call
// sites never need to repeat `activeTeamId ?? userTeamIds[0]`. The store clamp
// keeps activeTeamId valid once teams load, so this fallback only covers the
// pre-load window.
export const useActiveTeamId = (): string | undefined =>
  useTeamStore((s) => s.activeTeamId ?? s.userTeamIds?.[0] ?? undefined);
